/**
 * Shell, identity and the messages every surface can reach: the app frame,
 * language switching, sign-in, navigation labels and the generic error and
 * not-found states.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const common = {
  "clinicMaintenance.warning": "Maintenance starts at {deadline} (in {seconds} seconds). Please save your work and leave the clinic.",
  "clinicMaintenance.closed": "The clinic is under maintenance",
  "clinicMaintenance.description": "Access will return when the work is complete. Unsaved edits remain on this page. You can switch clinic or sign out.",
  "clinicMaintenance.recheck": "Check again",
  "clinicMaintenance.choose": "Switch clinic",
  "clinicMaintenance.developer": "Clinic maintenance: developer access",
  'organizations.choose': 'Choose an organization',
  'organizations.notificationUnavailable': 'This notification is unavailable or you no longer have access to it.',
  'organizations.empty': 'No organizations are available for this account.',
  'organizations.add': 'Add organization',
  'organizations.createTitle': 'New organization',
  'organizations.createUnsaved': 'New organization details',
  'organizations.createOptionsFailed': 'Organization creation is unavailable.',
  'organizations.createForbidden': 'You may not create organizations.',
  'organizations.name': 'Organization name',
  'organizations.calendar': 'Working calendar',
  'organizations.timeZone': 'Time zone',
  'organizations.defaultLanguage': 'Default language',
  'organizations.createConflict': 'This request was already saved with different details.',
  'organizations.createFailed': 'Could not create the organization.',
  'organizations.creating': 'Creating',
  'organizations.createSubmit': 'Create organization',
  'organizations.createCancel': 'Cancel',
  'app.generationChanged': 'The application data was replaced. This page can no longer save changes. Copy any unsaved text before reloading.',
  'app.generationReload': 'Reload application',
  'app.name': 'Terysling',
  // Sign-in's one-line self-description, rendered there alone: inside the
  // shell the header's line belongs to quick access, by operator decision.
  'app.descriptor': 'Clinical workspace',
  'app.skipToContent': 'Skip to main content',
  // WHAT THE BROWSER TAB SAYS, on every route: what this tab is, then whose
  // product it is. The connector belongs to the translator — a Hebrew tab is
  // read from the other end, and the mark between the two facts is that
  // reading's grammar rather than this file's punctuation.
  'app.documentTitle': '{page} — {brand}',
  // The tab's word for a module's own workspace, where every other address
  // names its destination. A module's NAME is a compiled contribution, and the
  // route table that answers the tab is the eager chunk every address is
  // decided in: reaching for the name there would pull each module's whole
  // frontend into the first download, for the routes that never open one.
  'app.moduleWorkspace': 'Module workspace',
  'app.loading': 'Loading',
  // Shown in place of the whole authenticated screen once the session is known
  // gone. It states the fact and nothing else: no clinic, no practitioner, no
  // patient, and no hint of what was on screen a moment earlier.
  'app.sessionEnded': 'Your session has ended. Returning to sign in…',

  // The developer credit at the foot of both frames. Two messages rather than
  // one sentence because the name stands in its own bidi isolate — see
  // `shared/branding/DeveloperCredit`, which explains why.
  'app.developedBy': 'Developed by',
  // A company name, spelled the way its owner spells it, so it is identical in
  // every catalogue exactly like the product's own name above.
  'app.developer': 'Mislab IL',

  'releaseVerification.cancel': 'Keep verifying',
  'releaseVerification.finish': "Finish verification and open access",
  'releaseVerification.confirm': "This opens production to enabled users and starts background processing. The application will restart. Unsaved forms on this page will remain open.",
  'releaseVerification.confirmAction': "Open production",
  'releaseVerification.progress': "Opening access. Reconnecting automatically…",
  'releaseVerification.loading': "Reading release status…",
  'releaseVerification.unavailable': "The result could not be confirmed. Check the status before trying again.",
  'releaseVerification.forbidden': "Only a developer may complete release verification.",
  'releaseVerification.stale': "This release has changed. Refresh its status before continuing.",
  'releaseVerification.failed': "Completion failed. Contact the release operator; existing data is preserved.",
  'releaseVerification.checkStatus': "Check status",
  'releaseVerification.refreshFailed': "Release completed, but the session could not be refreshed. Try checking again.",
  'releaseVerification.bannerLabel': 'Release verification',
  'releaseVerification.eyebrow': 'Production release verification',
  'releaseVerification.banner': 'Release verification is active. Anything you create or change now may be lost.',
  'releaseVerification.title': 'Production release verification is active',
  'releaseVerification.description':
    'Only release-maintenance users are allowed in while the production switch is being checked. If the release is rolled back, work created in this window may be discarded.',
  'releaseVerification.acknowledge': 'I understand',

  'language.label': 'Language',
  'language.en': 'English',
  'language.he': 'Hebrew',
  'language.ru': 'Russian',
  /*
   * ENDONYMS: what each language calls itself, and deliberately the same string
   * in every catalogue.
   *
   * The group above stays translated because a different control needs it — the
   * PATIENT's preferred language, which a clinician reads in their own
   * interface. The language picker has the opposite requirement: someone left
   * in an interface they cannot read has to find their own language in the
   * list, and translating the options is exactly what stops them.
   *
   * Two ids per language rather than one reused id, because the two facts
   * diverged the moment the third language arrived: the Russian catalogue
   * translates the NAME of Hebrew while still spelling its endonym `עברית`, and
   * nothing about the patient field wants an endonym.
   */
  'language.nativeEn': 'English',
  'language.nativeHe': 'עברית',
  'language.nativeRu': 'Русский',
  'language.saving': 'Saving language',
  'language.saveFailed': 'Language was not saved. Try again.',

  // The searchable choice (`shared/ui/Combobox`). Chrome, like the
  // table's below: one control serves every list too long to read, so a
  // namespace per surface would be three translations of one popover.
  // Not "Select…": the placeholder stands where an answer will be, and a
  // control that instructs the reader says nothing about its own state.
  'combobox.placeholder': 'Not selected',
  'combobox.search': 'Search',
  'combobox.empty': 'Nothing matches',
  // Said while an answer is still in flight, never instead of one: a list
  // answered by the server is empty for two different reasons and they must
  // not share a sentence.
  'combobox.searching': 'Searching…',
  // A choice of several: the closed field counts what is chosen, and each
  // chip's remove button names the value it takes back — the same word on
  // every chip would not say which one it removes.
  'combobox.chosenCount': '{count} selected',
  'combobox.unchoose': 'Remove {name}',

  // The eye on a masked field (`shared/ui/PasswordInput`). One name in both
  // states: the toggle's pressed state says whether the value is showing.
  'passwordInput.show': 'Show password',
  'passwordInput.showSecret': 'Show secret',
  // The rule a NEW password is held to, in the server's own numbers
  // (`GET /api/v1/auth/password-policy`), never a copy of them. A label and
  // two bare numbers, because the bounds are the server's to change and this
  // catalogue has no plural machinery to agree a noun with them.
  'passwordInput.policyHint': 'Password length in characters: {min} to {max}.',
  'passwordInput.requireDigit': 'Include at least one digit.',
  'passwordInput.requireUppercase': 'Include at least one uppercase letter.',
  'passwordInput.requireSpecial': 'Include at least one special character (punctuation or a symbol).',
  'passwordInput.policyUnavailable': 'The password requirements could not be loaded. Reload the page to try again.',

  // The one word every panel closes with (`shared/ui/PanelClose`). It replaced
  // nine surface-owned ids, six of which rendered this identical string. It
  // does not name what it closes: the panel's own region label already does,
  // and a named form in Hebrew stops containing the bare word.
  'panel.close': 'Close',
  'sheet.done': 'Done',

  /*
   * THE DOCKED CODE PICKER (`shared/coding/CatalogueBrowser`). One panel serves
   * every long code catalogue the product picks from — diagnoses, oncology
   * topography and morphology, and the billable services — and its chrome is
   * the same question every time: my saved codes, or the catalogue.
   *
   * The copy is here rather than under one surface because it belongs to none
   * of them. It used to live under `diagnosis.dictionary*`, and the moment
   * Finance rendered the same panel that namespace would have meant either a
   * second translation of "Add folder" or Finance reading Clinical's strings.
   *
   * What each caller still owns is the part that names ITS catalogue: the
   * panel's title, the catalogue tab's name, its search placeholder and
   * whatever the catalogue body says while it loads or finds nothing.
   */
  'codePicker.tabs': 'Where to look for a code',
  'codePicker.tabPersonal': 'My codes',
  'codePicker.searchLabel': 'Find a code',
  'codePicker.star': 'Save {name} to my codes',
  'codePicker.unstar': 'Remove {name} from my codes',
  'tree.expandNamed': 'Expand {name}',
  'tree.collapseNamed': 'Collapse {name}',
  'tree.enterNamed': 'Open {name}',
  'tree.back': 'Back',
  'tree.renameNamed': 'New name for {name}',
  'codePicker.selectNamed': 'Use {name}',
  // The personal tab's own loading and failure. Separate from whatever the
  // catalogue says: they are two requests, and one of them failing while the
  // other answered is the case a shared sentence hides.
  'codePicker.personalLoading': 'Loading…',
  'codePicker.personalLoadFailed': 'Your saved codes could not be read.',
  'codePicker.personalEmpty': 'You have not saved any codes yet.',
  'codePicker.personalEmptyDetail': 'Starring a code in the catalogue adds it here.',
  // Said instead of the line above to an account that cannot write the list at
  // all: telling it to star a code would name a control it is not shown.
  'codePicker.personalEmptyNoGrant': 'Your account cannot save codes to this list.',
  // A folder IS a set: `folderId: null` is the flat starred list, and a named
  // folder is the group. One idea with one name.
  'codePicker.shortlist': 'Starred',
  'codePicker.folderName': 'New folder',
  'codePicker.folderCreate': 'Add folder',
  'codePicker.folderEmpty': 'This folder is empty.',
  'codePicker.showLabel': 'Show',
  'codePicker.showAll': 'Everything',
  'codePicker.showFolders': 'Folders only',
  'codePicker.showCodes': 'Codes only',
  'codePicker.shortlistNoMatches': 'None of your saved codes matches.',
  'codePicker.itemMenu': 'Actions for {name}',
  'codePicker.menuMoveTo': 'Move to',
  'codePicker.menuRemove': 'Remove from my codes',
  'codePicker.folderMenu': 'Actions for folder {name}',
  'codePicker.menuRename': 'Rename folder',
  'codePicker.menuDelete': 'Delete folder and the codes in it',
  'codePicker.folderNameTaken': 'You already have a folder with that name.',
  'codePicker.forbidden': 'You do not have permission to change your saved codes.',
  'codePicker.gone': 'That folder or code is no longer saved.',
  'codePicker.invalid': 'That code cannot be saved.',
  'codePicker.writeFailed': 'Your saved codes could not be changed. Try again.',

  // The shared registry table (`shared/ui/DataTable`). Its copy is chrome, not
  // domain: every surface that renders the table says these same words, so a
  // second namespace per surface would be four translations of one control.
  'table.columnFilter': 'Filter {column}',
  'table.columnFilterReset': 'Clear the {column} filter',
  'table.columnFilterSearch': 'Search',
  'table.columnFilterSelectVisible': 'Select visible',
  'table.columnFilterClearVisible': 'Clear visible',
  'table.columnFilterClear': 'Clear',
  'table.columnFilterNoValues': 'No values',
  // The instant filter in the leading header cell (`shared/ui/instantFilter`).
  // Both strings NAME THE COLUMNS it matches, because it is the one control on
  // a registry that narrows more than the column it stands in, and a reader
  // who cannot see which facts it reads has to discover them by typing.
  'table.instantFilterPlaceholder': 'Filter by {columns}',
  // The accessible name adds the table, which the placeholder cannot: the
  // heading beside it is on screen for a reader who sees it, and is the
  // sentence's missing subject for a reader who does not.
  'table.instantFilterLabel': 'Filter {table} by {columns}',
  // The free-text criterion box every column's funnel offers, and the words
  // around it. It is a different control from `columnFilterSearch` above: that
  // one narrows the list of VALUES on screen, this one narrows the TABLE.
  'table.filterContains': 'Contains',
  'table.criterionApply': 'Apply',
  // Always on screen rather than behind a help affordance: `/pattern/` is a
  // rule nobody guesses, and a box that silently reads a stray pair of slashes
  // as a regular expression is worse than one that says it does.
  'table.criterionHint': 'Any part of the value, or /pattern/ for a regular expression.',
  'table.criterionTooLong': 'The criterion is too long.',
  'table.criterionInvalidPattern': 'This is not a valid regular expression.',
  // The closed-enum columns, whose Apply ticks values instead of sending text.
  'table.criterionNoMatch': 'No value in this column matches.',
  // What a column is matched AGAINST, where it shows something else. Prefixed
  // to the sentence above by the funnel that needs it.
  'table.criterionOnDate': 'Matched against the date as YYYY-MM-DD.',
  'table.criterionOnInstant': 'Matched against the date and time as shown.',
  'table.criterionOnBytes': 'Matched against the size in bytes.',
  'table.criterionOnValues': 'Applying ticks every value that matches.',
  // The client-resolved date and number columns (`useRegistryTable`), whose
  // criterion reads the rendered text while the range reads the magnitude.
  'table.criterionOnShownValue': 'Matched against the value as shown.',
  // The two ends of a range funnel, NAMED FROM THE COLUMN they narrow. A
  // per-surface pair of ids is how the laboratory's `Recorded` column came to
  // offer a `Document date from` box: the same registry, the same control, and
  // a heading that disagreed with the field under it. Where the unit matters
  // more than the heading — a size in kilobytes — the column passes its own
  // words instead.
  'table.rangeFrom': '{column} from',
  'table.rangeTo': '{column} to',
  // THE TOOLBAR'S UNDO-EVERYTHING, one per registry and worded once. Four
  // surfaces used to pass wording of their own through a `resetLabel` prop —
  // the patients list, the EMR journal, the files registry and one external
  // module's flow — so one action read as "Reset filters" on most of the
  // product and "Clear filters" on the rest, and the Hebrew catalogue carried
  // six different strings for it. The prop is gone: this is the wording.
  'table.resetFilters': 'Clear filters',
  // The same act, named for its table where a reader hears it rather than
  // where they read it. Two registries on one screen bring two of these, and
  // the two visible words are true of both; the accessible name opens with
  // them and then says which table, so what is spoken from the screen still
  // reaches the control.
  'table.resetFiltersOf': 'Clear filters of {table}',
  // THE ADD ACTION, one word for every registry in the product. Not "Add file"
  // or "Add EMR entry": the table the button stands on says what the row will
  // be, and naming the object again made one action read as four.
  'table.add': 'Add',
  // TAKING A COLUMN OFF THE TABLE, from the heading it stands on. There is no
  // "Show {column}" beside it any more: a hidden column leaves no strip behind
  // to carry one, and the chooser's ticks are the way back.
  'table.hideColumn': 'Hide {column}',
  // THE COLUMN CHOOSER, opened from the header's corner. The heading of its
  // menu is the bare noun: the control that opened it already named the table,
  // and repeating it would write the table's name twice in one dropped panel.
  'table.columns': 'Columns',
  'table.columnsChoose': 'Choose the columns of {table}',
  // THE MARK, IN WORDS. The mark on the button says "something is off this
  // table" in colour, which says it to nobody reading through a screen
  // reader, so the control's own name carries it. No count: a counted
  // sentence needs plural grammar this catalogue does not carry, and the
  // menu itself is one press away with the ticks that answer "which".
  'table.columnsChooseHidden': 'Choose the columns of {table} — some are hidden',
  // THE STRONGER FACT, and the reason it is worth its own sentence: rows are
  // missing because of a column the reader cannot see to widen.
  'table.columnsChooseFiltering': 'Choose the columns of {table} — a hidden column is filtering it',
  // A column the table will not hide, because the row's editor lives in it.
  'table.columnFixed': 'Always shown',
  // And the other refusal: a table cannot be emptied of its columns, so the
  // last one standing keeps neither its hide control nor a pressable tick.
  'table.columnLast': 'The last column on the table',
  // A hidden column that is still keeping rows away. Its own funnel went off
  // the table with it, so the menu states it on the column's row instead.
  'table.columnNarrowed': '{column} is hidden and still filtering the table',
  // Named for the ROW, because a table of them announces one control per row
  // and "Expand" alone would be the same sentence a dozen times.
  'table.expandRow': 'Expand {name}',
  'table.collapseRow': 'Collapse {name}',
  'table.expandGroup': 'Expand group {name}',
  'table.collapseGroup': 'Collapse group {name}',
  // The tick column, for a table with an action over several rows. Named for
  // the row for the same reason the expander is.
  'table.selectRow': 'Select {name}',
  // THE MASTER TICK NAMES THE ROWS SHOWN, never "all". Several registries here
  // are cursor-paginated, so a control claiming every row would be claiming
  // rows that are not loaded and that no action could reach.
  'table.selectShown': 'Select the rows shown',
  'table.clearSelection': 'Clear the selection',
  'table.filters': 'Filters',
  'table.filterSheet': 'Filters of {table}',
  'table.rowActions': 'Actions for {name}',
  // WHAT AN ACT OVER THE TICKED ROWS DID WHEN IT DID NOT TAKE ALL OF THEM. The
  // rows are NAMED rather than counted — a count of failures is not something a
  // clinician can act on — and only the first two, because a notice long enough
  // to scroll is one nobody reads. The last line says the ticks are still
  // standing, which is what makes pressing again a resume and not a restart.
  // What WAS written is said by the surface, in its own words for its own act.
  'table.bulkRefused': 'The server refused {names}.',
  'table.bulkRefusedMore': 'The server refused {names} and {count} more.',
  'table.bulkRefusedDetail': 'Every row that was not done is still selected.',
  // The funnel entry for a row that states nothing in this column. The cell
  // itself renders blank (`EmptyValue`); the absence is a value HERE so it can
  // be ticked, counted and asked for on its own, which is the whole reason a
  // blank cell is safe.
  'table.notSet': 'Not set',
  // THE PAGE CONTROLS, in one band below the rows (#398). A cursored read is never
  // counted, so these words state the rows on screen and the page they came
  // from and never a total: "1–100 of 4000" is a sentence this server has
  // never said, and a pager that infers it is wrong on the last page of every
  // filtered list.
  'table.pageRow': 'Row {from}',
  'table.pageRows': 'Rows {from}–{to}',
  'table.pageNumber': 'Page {page}',
  'table.previousPage': 'Previous',
  'table.nextPage': 'Next',
  // The band's landmark, one per registry.
  //
  // IT NAMES THE TABLE, NOT "THE TABLE". A screen may show two paged
  // registries at once — Control Center puts the protocol releases over the
  // assignment rules, and the print-form bindings over the data schemas — and
  // a band named for its place alone leaves two landmarks answering to one
  // name, so "the pages below the table" identifies neither. `{table}` is the
  // grid's own accessible name, handed to the band by `DataTable` rather than
  // restated by a surface.
  'table.pagesBelow': 'Pages, below {table}',
  // How long a page is. Named for its band and its table for the reason the
  // landmark is: the control is drawn once more for every registry on the
  // screen, and a reader who tabs into one has to hear which table's they
  // reached. The options themselves are bare numbers, so this name is also
  // what says what the number counts.
  'table.pageSizeBelow': 'Rows per page, below {table}',
  // What stands where the rows would be when the FILTERS emptied the table.
  // Chrome, not domain: the surface's own empty copy answers a registry that
  // is genuinely empty, this pair answers one that was narrowed to nothing.
  'table.noMatches': 'No rows match the filters',
  'table.noMatchesDetail': 'Widen or reset the column filters.',
  // The filter assistant in the leading header cell: describe the search, get
  // the column filters. Named for the TABLE, because the corner it stands in
  // has no heading of its own and "Describe what to find" alone would be the
  // same sentence on every registry a reader has open.
  'table.assistantOpen': 'Describe what to find in {table}',
  'table.assistantTitle': 'Describe what to find',
  'table.assistantIntro': 'Say what you are looking for and the {table} columns are filtered for you.',
  // ON SCREEN, NOT IN A HELP PAGE. A reader decides whether to type a patient's
  // name into this box right here, and the reason it is safe — the rows never
  // travel — has to be readable at that moment.
  'table.assistantPrivacy':
    'Only the column headings and your words are sent. No row, value or count leaves the clinic.',
  'table.assistantLabel': 'What are you looking for?',
  'table.assistantPlaceholder': 'registered this month, name contains Levin',
  'table.assistantAsk': 'Suggest filters',
  'table.assistantRetry': 'Ask again',
  'table.assistantApply': 'Apply filters',
  'table.assistantClose': 'Close',
  'table.assistantWorking': 'Working out the filters…',
  // Said before Apply, because applying REPLACES what is in force rather than
  // narrowing on top of it.
  'table.assistantReady': 'Applying these replaces the filters in force.',
  'table.assistantNoFilters': 'No column of this table can be narrowed by that.',
  // The proposed filters, read back column by column. Words, not operators:
  // the reader is checking a proposal, not reading a query.
  'table.assistantContains': 'contains',
  'table.assistantFrom': 'from',
  'table.assistantTo': 'to',
  // What the reader asked for that a column filter cannot express — an
  // exclusion, an ordering, a fact no column holds. Said rather than silently
  // dropped: a filter set that quietly answered half the question would look
  // like it answered all of it.
  'table.assistantUnmatched': 'Column filters cannot ask for:',
  'table.assistantKeyMissing': 'This account has no OpenAI key configured.',
  'table.assistantForbidden': 'This account may not run AI prompts.',
  'table.assistantFailed': 'The filters could not be suggested. Try again.',

  // The window splitter between the workspace and the context panel. It names
  // what it moves rather than the gesture: a screen reader announces the role
  // and the value already, and "drag" is a word for one of the two ways it
  // works.
  'shell.resizeContext': 'Context panel width',

  'appearance.label': 'Appearance',
  // The four axes of the picker, each naming its group.
  'appearance.theme': 'Theme',
  'appearance.mode': 'Mode',
  'appearance.light': 'Light',
  'appearance.dark': 'Dark',
  // The mode dial's third position inside the Telegram Mini App only: no
  // override, the product follows the client's light or dark.
  'appearance.followTelegram': 'Follow Telegram',
  // "Text size" rather than "scale" or "zoom": the dial moves the type, and
  // everything else follows because it is measured in it. A clinician looking
  // for larger text should not have to know that.
  'appearance.scale': 'Text size',
  'appearance.scaleValue': '{percent}%',
  'appearance.density': 'Density',
  'appearance.densityCompact': 'Compact',
  'appearance.densityCozy': 'Cozy',
  'appearance.densityRelaxed': 'Relaxed',
  // Theme names. `Terys` is a proper name, kept as-is in every catalogue like
  // the product's own; the other three are ordinary words and translate.
  'appearance.themeTerys': 'Terys',
  'appearance.themeEmerald': 'Emerald',
  'appearance.themeAmber': 'Amber',
  'appearance.themeGraphite': 'Graphite',
  // The white-label appearances, and proper names like `Terys` above: the
  // vendor's own name, spelled the way the vendor's brand guide spells it
  // (lowercase everywhere it is set as a wordmark; here it is running text in
  // a list of themes, so it takes the list's sentence case).
  //
  // THE MARK IS LATIN IN EVERY CATALOGUE AND THE WORD BESIDE IT IS NOT. The
  // mark itself never declines, for the reason `branding/brand.ts` gives, and
  // it has to stay Latin for a second reason: the rule that keeps the vendor's
  // name out of shared copy matches the mark, so a transliteration would be a
  // shared surface naming the vendor that nothing could see. `Brand` is an
  // ordinary English word, so it is translated like one.
  //
  // There are two labels because the vendor has two answers about colour: the
  // plain one is its shipped software, the `Brand` one is its print brand
  // guide. The theme file each label names carries the difference and the
  // reason.
  'appearance.themeSmartomica': 'Smartomica',
  'appearance.themeSmartomicaBrand': 'Smartomica Brand',

  'login.title': 'Sign in',
  'login.subtitle': 'Sign in to your clinic workspace.',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in',
  'login.usernameRequired': 'Enter your username.',
  'login.passwordRequired': 'Enter your password.',
  // Deliberately identical for unknown account and wrong password: the UI must
  // not disclose whether an account exists.
  'login.telegramNotLinked': "Link Telegram to your Terysling account in your profile before using this button. You can sign in with your password below.",
  'login.telegramInactive': "Telegram sign-in is unavailable for this account. Contact your clinic administrator.",
  'login.telegramExpired': "This Telegram sign-in has expired or was already used. Press the event button in Telegram again, or sign in below.",
  'login.telegramInvalid': "Telegram sign-in could not be verified. Press the event button in Telegram again, or sign in below.",
  'login.failed': 'Sign in failed. Check your username and password.',
  'login.maintenanceRestricted': 'Sign-in access is restricted during maintenance. Try again after maintenance is complete.',
  'login.throttled': 'Too many sign-in attempts. Try again later.',
  'login.unavailable': 'The service is unavailable right now. Try again later.',

  'shell.primaryNavigation': 'Primary',

  /* The navigation panel. It has no title of its own — the head carries the
     claiming route's way back instead — so what is left here is its two
     controls, each stated as the ACTION it performs: the chevrons at the head
     of the standing panel fold it to the rail, and the chevrons at the top of
     the rail unfold it. Two words for two controls, so "what did I just
     press" has one answer wherever the pointer was. */
  'shell.navCollapse': 'Collapse navigation',
  'shell.navExpand': 'Expand navigation',
  /* The handheld drawer: the header control that opens it, the control that
     closes it, and the name of the dialog the navigation stands in while it
     is up. It promises to SHOW a navigation that is not on screen at all,
     which is a different thing from unfolding a rail that is. */
  'shell.navShow': 'Show navigation',
  'shell.navHide': 'Hide navigation',
  'shell.navDrawer': 'Navigation',
  /* The handheld context sheet: the shell's own way back to the page, which
     keeps the route's claim, and the control that raises the sheet again.
     Neither is the panel's close — that word stays `panel.close`. */
  'shell.contextHide': 'Hide context panel',
  'shell.contextShow': 'Show context panel',
  'shell.accountRegion': 'Account and workspace',
  'shell.activeClinic': 'Active organization',
  'shell.clinicSwitchFailed': 'The active clinic could not be changed.',
  'shell.organizationCurrent': 'Current',
  /* The organization dialog's own way out, and the only act in its action
     row. It is not the panel head's `panel.close`:
     `shared/ui/PanelClose.rule.test.ts` keeps the two apart. */
  'shell.organizationSwitchClose': 'Close',
  'shell.organizationContextChanged': 'The organization context changed. Refresh before continuing.',
  'shell.organizationContextRefresh': 'Refresh organization context',
  'shell.elevation': 'Developer elevation',
  'shell.elevationActive': 'Developer elevation is active',
  'shell.elevationOff': 'Not elevated',
  'shell.elevationEnds': 'Ends',
  'shell.elevationNoExpiry': 'Only when ended',
  'shell.elevationDeactivate': 'End elevation',
  'shell.elevationDeactivateFailed': 'Developer elevation could not be ended.',
  'shell.elevationReason': 'Reason for developer elevation',
  'shell.elevationTemporary': 'Elevate for one hour',
  'shell.elevationPersistent': 'Keep elevated in development',
  'shell.elevationActivateFailed': 'Developer elevation could not be activated.',
  'shell.signOut': 'Sign out',
  'shell.signingOut': 'Signing out',
  'shell.sessionExpired': 'Your session ended. Sign in again.',

  'shell.unsavedTitle': 'This work has not been saved',
  'shell.unsavedBodyOne': 'Leaving now discards work the server has never received:',
  'shell.unsavedBodyMany': 'Leaving now discards {count} pieces of work the server has never received:',
  'shell.unsavedStay': 'Stay on this page',
  'shell.unsavedDiscard': 'Discard and leave',

  // Names the right column's landmark; its CONTENT is the active route's.
  'shell.contextPanel': 'Context panel',

  'notFound.title': 'Page not found',
  'notFound.detail': 'This page does not exist.',
  'notFound.back': 'Go to Dashboard',

  'error.invalidReadRequest': 'The server rejected the page request',
  'error.invalidReadRequestDetail': 'Ask an administrator to check the page address and request.',
  'error.title': 'Something went wrong',
  'error.detail': 'The page could not be loaded.',
  'error.retry': 'Try again',
  'error.forbidden': 'You do not have access to this',
  'error.forbiddenDetail': 'Ask an administrator if you need it.',
  /* A 2xx whose body did not carry what the surface renders. Separate from
     the generic failure because the read SUCCEEDED: repeating it returns the
     same incomplete answer, so the panel that shows this offers no retry. */
  'error.invalidResponse': 'The server response was incomplete.',

  /*
   * A REFRESH FAILED WHILE THE CONTENT BESIDE IT IS STILL REAL.
   *
   * Shared rather than per-surface because five widgets say it — four on the
   * dashboard and the recent-Patient card — and a state described in two
   * different sentences on two screens reads as two different states.
   *
   * It is deliberately NOT the "unavailable" wording. Calling retained content
   * unavailable contradicts the rows sitting under the sentence, and keeping
   * those rows is only honest because this line is there.
   */
  'error.notRefreshed': 'Not refreshed',
  'error.notRefreshedDetail': 'Showing what the server last confirmed. It may have changed.',

  /* Each destination is a WORD and a line. The word is the accessible name of
     its tile, so a list of links reads as a list of destinations; the line
     says what is inside without promising a count nobody has read. */
  'nav.patients': 'Patients',
  'nav.patientsDescription': 'Registry, charts and encounters',

  'nav.schedule': 'Schedule',
  'nav.scheduleDescription': 'The day and its appointments',

  'nav.tasksDescription': 'Clinic work and follow-ups',

  'nav.leads': 'Leads',
  'nav.leadsDescription': 'New requests and patient onboarding',

  // The destination itself; the copy for each surface inside it lives in the
  // `settings` and `controlCenter` modules, because the navigation owns the
  // entry and not the surfaces.
  "nav.controlCenter": "Clinic management",
  "nav.controlCenterDescription": "Organization, staff, forms and connections",

  /*
   * THE WAY BACK, ONCE, FOR EVERY SURFACE THAT HAS ONE.
   *
   * Each one names its destination exactly as the tile above names it, because
   * a clinician is being sent to a screen the shell already names. These used
   * to be written per screen, and `/` alone accumulated three words for it —
   * the workspace, home and the Dashboard — each correct where it stood.
   * `shared/routing/wayBack.tsx` is the one control that reads them.
   *
   * SINGULAR AND PLURAL ARE TWO DESTINATIONS, not one word with a slip in it.
   * `backToPatients` is the registry the primary navigation names; the singular
   * is the OPEN chart, and it is the word a surface inside a patient — an EMR
   * entry, the laboratory, Finance — puts in the head to climb one rung. A
   * locale that renders them alike leaves a clinician unable to tell leaving
   * this person from leaving everybody.
   */
  'nav.backToDashboard': 'Back to Dashboard',
  'nav.backToPatients': 'Back to Patients',
  'nav.backToPatient': 'Back to Patient',
  'nav.backToSchedule': 'Back to Schedule',
  'nav.backToControlCenter': 'Back to Clinic management',
  'nav.backToHelp': 'Back to Help',

  /*
   * The header's quick-access bookmarks. The label names the landmark and the
   * empty row's invitation; the customize control opens the catalogue of the
   * destinations the left panels offer. Destination labels are deliberately
   * NOT restated here — each pill reads the id its own surface renders, so one
   * section cannot answer to two names on two screens.
   */
  'quickAccess.label': 'Quick access',
  'quickAccess.customize': 'Customize quick access',
  'quickAccess.hint':
    'Chosen sections stay one press away on every screen. Drag a tile out of a navigation panel, or a patient out of the register, and let it go anywhere on the top strip — or press the pin beside it: a patient’s section or an entry’s included. Where you let it go is where it stands, and a pill dragged along the row moves.',
  'quickAccess.groupWorkspace': 'Workspace',
  'quickAccess.groupProfile': 'Profile',
  'quickAccess.groupChosen': 'In the row',
  'quickAccess.pin': 'Add {name} to quick access',
  'quickAccess.unpin': 'Remove {name} from quick access',
  'quickAccess.unpinUnavailable': 'Remove the unavailable bookmark from quick access',
  'quickAccess.dropHint': 'Drop to add to quick access',
  'quickAccess.moveHint': 'Drop to move it here',
  'quickAccess.moveEarlier': 'Move {name} earlier in quick access',
  'quickAccess.moveLater': 'Move {name} later in quick access',
  /* A bookmark's accessible name: the section it is a view of, or the record
     it belongs to, before the section's own word — three sections are called
     "Settings", and a pill reading the bare word would not say which. */
  'quickAccess.bookmarkName': '{within}: {label}',
  'quickAccess.recordName': '{subject}: {label}',
  'quickAccess.recordViewName': '{subject}: {within}, {label}',
  'quickAccess.entrySubject': '{name}, entry {code}',
  'quickAccess.entryOnly': 'Entry {code}',
  'quickAccess.unavailable': 'No longer available',
  'common.unknownValue': 'Unknown value',
  /* HOW A VERSION NUMBER IS WRITTEN, in one place. Three surfaces built the
     same `v{n}` in code and a fourth kept a catalogue id of its own for it; a
     locale that abbreviates it differently, or writes it on the other side of
     the number, has one line to change. */
  'common.version': 'v{version}',
  /* A RECORD'S REVISION, named in one place. A revision is one saved state in
     a record's own history — what a save, a correction, an archive or a
     restore appends, and the number a concurrent write is checked against —
     while `version` above is a published release of software, a protocol, a
     module or a dictionary. Sentences that inflect the word keep their own ids
     and use the same word; see docs/web-reference/localization.md. */
  'common.revision': 'Revision',
  'common.revisionNumber': 'Revision {revision}',
  'common.revisionHistory': 'Revision history',
  /* THE UNIT BESIDE A FILE SIZE, in the catalogue rather than in the helper
     that rounds the number, because a locale that writes its own abbreviation
     — the Russian catalogue already spells these in its own script in the
     size filter beside this — cannot reach a Latin literal concatenated in
     code. The number is placed by the line, so a locale that puts the unit
     first can. */
  'common.bytes': '{size} B',
  'common.kilobytes': '{size} KB',
  'common.megabytes': '{size} MB',
  'time.invalidLocal': 'Choose a local date and time that exists in the clinic time zone.',
  /* The shared coded-value picker's status lines (`shared/coding/CodingField`),
     spoken by every surface that searches a dictionary. What the search box
     PROMISES — which dictionary — stays with each surface's own key. */
  'coding.loading': 'Loading dictionary matches',
  'coding.empty': 'No dictionary matches',
  'coding.failed': 'Could not search the dictionary.',
} as const
